/**
 * PaytableSlider.jsx — Hardened carousel engine for the paytable overlay
 *
 * Owns the entire animation pipeline: anime.js-driven translateX track,
 * distance-based opacity fade, infinite clone loop, drag/swipe handling,
 * arrow navigation, pagination dots.
 *
 * This code has been production-hardened:
 * - animationRef is nulled after every anime.remove() + complete callback
 * - Navigation throttle (100ms) on all paths (arrows, dots, drag)
 * - goToSlide blocked during clone teleport window (isJumpingRef)
 * - slideWidthRef caches layout to avoid 60fps reflow
 * - isTransitioning is a ref (no re-render overhead for navigation guards)
 *
 * DO NOT modify easing curves or durations without designer approval.
 */

import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import anime from "animejs";
import {
  TOTAL_SLIDES,
  NAV_THROTTLE_MS,
  CONTENT_WIDTH_CONFIG,
  getDeviceConfig,
  getPaytableArrowLayout,
  PAYTABLE_TUNED_LANDSCAPE_CHROME,
  getPaytableTunedLandscapeSideOffset,
} from "./paytableConfig";
import { PixiSettings } from "../../../../pixiSettings";
import { getUITextureAsDataURL } from "../../../../assets";
import PaytableSlide from "./slides/PaytableSlide";

const PaytableSlider = ({
  slides,
  dimensions,
  device,
  orientation,
  chrome,
  toolbar,
  isPortrait,
  shouldRender,
  contentWidth,
  glowSrc,
  footerMarginTop = 0,
  isTunedMobileLandscape = false,
  isWideDesktop = false,
  landscapeChromeSideOffset = null,
  isHidden = false,
}) => {
  // ── Carousel state ──
  const [currentSlide, setCurrentSlide] = useState(0);
  const isTransitioningRef = useRef(false);
  const isJumpingRef = useRef(false);
  const isPaginationRef = useRef(false);
  const sliderRef = useRef(null);
  const trackRef = useRef(null);
  const trackPosObj = useRef({ x: 0 });
  const animationRef = useRef(null);

  // Drag/swipe state (Pointer Events — unifies mouse + touch, so desktop can drag too)
  const dragStartX = useRef(null);
  const dragCurrentX = useRef(null);
  const isDragging = useRef(false);
  const dragStartTime = useRef(null);
  const dragStartContainerX = useRef(null);
  // Pointer id of the active drag, so capture can be force-released if the
  // gesture is orphaned (e.g. overlay hidden mid-swipe by the iOS guard).
  const dragPointerId = useRef(null);

  // Cached slide width — sub-pixel width avoids cumulative translateX drift
  const slideWidthRef = useRef(0);

  const readSlideWidth = useCallback((el) => {
    if (!el) return 0;
    return el.getBoundingClientRect().width;
  }, []);
  // Navigation throttle — prevents rapid-fire from automated tools or accessibility
  const lastNavTimeRef = useRef(0);

  // ── Slide area padding — computed from device/orientation ──
  const slideAreaPad = useMemo(() => {
    if (contentWidth != null) {
      return Math.max(0, Math.round((dimensions.width - contentWidth) / 2));
    }
    if (
      isPortrait &&
      (device === PixiSettings.DEVICE.MOBILE ||
        device === PixiSettings.DEVICE.MOBILE_SMALL ||
        device === PixiSettings.DEVICE.TABLET)
    )
      return 0;
    const fraction = getDeviceConfig(CONTENT_WIDTH_CONFIG, device, orientation);
    return (dimensions.width * (1 - fraction)) / 2;
  }, [contentWidth, isPortrait, device, orientation, dimensions.width]);

  // ── Opacity fade engine ──

  const fadeEasing = useMemo(() => anime.easing("easeInOutCubic"), []);

  const calculateSlideAlpha = useCallback(
    (slideGlobalX, slideWidth) => {
      const distanceFromCenter = Math.abs(slideGlobalX);
      const fadeStartDistance = slideWidth * 0.1;
      const fadeEndDistance = slideWidth * 0.35;
      if (distanceFromCenter <= fadeStartDistance) return 1;
      if (distanceFromCenter >= fadeEndDistance) return 0;
      const t =
        (distanceFromCenter - fadeStartDistance) /
        (fadeEndDistance - fadeStartDistance);
      return 1 - fadeEasing(t);
    },
    [fadeEasing],
  );

  // Uses cached slideWidthRef to avoid layout reads at 60fps
  const updateSlideOpacities = useCallback(() => {
    if (!trackRef.current) return;
    const trackX = trackPosObj.current.x;
    const slideWidth = slideWidthRef.current;
    if (!slideWidth) return;
    const slideElements = trackRef.current.children;
    for (let j = 0; j < slideElements.length; j++) {
      const slideGlobalX = trackX + slideElements[j].offsetLeft;
      slideElements[j].style.opacity = calculateSlideAlpha(
        slideGlobalX,
        slideWidth,
      );
    }
  }, [calculateSlideAlpha]);

  // Get target track X position for a slide index (in pixels)
  const getTargetX = useCallback(
    (index) => {
      const trackEl = trackRef.current;
      if (trackEl?.children?.length) {
        const slideEl = trackEl.children[index + 1];
        if (slideEl) return -slideEl.offsetLeft;
      }
      const slideWidth =
        slideWidthRef.current || readSlideWidth(sliderRef.current);
      if (!slideWidth) return 0;
      return -((index + 1) * slideWidth);
    },
    [readSlideWidth],
  );

  // ── Initialize track position + reset to slide 0 when overlay opens ──
  useLayoutEffect(() => {
    if (!shouldRender || !sliderRef.current || !trackRef.current) return;
    slideWidthRef.current = readSlideWidth(sliderRef.current);
    // Always reset to slide 0 on overlay open — mark as jump to skip animation
    isJumpingRef.current = true;
    isTransitioningRef.current = false;
    const targetX = getTargetX(0);
    trackPosObj.current.x = targetX;
    trackRef.current.style.transform = `translateX(${targetX}px)`;
    updateSlideOpacities();
    isJumpingRef.current = false;
    setCurrentSlide(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender]);

  // ── Animation engine — anime.js driven translateX ──
  useEffect(() => {
    if (isDragging.current) return;
    if (!trackRef.current || !sliderRef.current) return;

    const targetX = getTargetX(currentSlide);
    const posObj = trackPosObj.current; // capture for cleanup

    if (animationRef.current) {
      anime.remove(posObj);
      animationRef.current = null;
    }

    if (isJumpingRef.current) {
      posObj.x = targetX;
      trackRef.current.style.transform = `translateX(${targetX}px)`;
      updateSlideOpacities();
      isJumpingRef.current = false;
      isTransitioningRef.current = false;
    } else {
      const duration = isPaginationRef.current ? 500 : 700;
      isPaginationRef.current = false;
      animationRef.current = anime({
        targets: posObj,
        x: targetX,
        duration,
        easing: "cubicBezier(.34, 1.30, .64, 1)",
        update: () => {
          if (trackRef.current) {
            trackRef.current.style.transform = `translateX(${posObj.x}px)`;
          }
          updateSlideOpacities();
        },
        complete: () => {
          animationRef.current = null;
          if (currentSlide >= TOTAL_SLIDES) {
            isJumpingRef.current = true;
            setCurrentSlide(0);
          } else if (currentSlide < 0) {
            isJumpingRef.current = true;
            setCurrentSlide(TOTAL_SLIDES - 1);
          } else {
            isTransitioningRef.current = false;
          }
        },
      });
    }

    return () => {
      if (animationRef.current) {
        anime.remove(posObj);
        animationRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide]);

  // Recalculate positions on resize
  useLayoutEffect(() => {
    if (!shouldRender || !sliderRef.current || !trackRef.current) return;
    if (animationRef.current) {
      anime.remove(trackPosObj.current);
      animationRef.current = null;
    }
    slideWidthRef.current = readSlideWidth(sliderRef.current);
    const targetX = getTargetX(currentSlide);
    trackPosObj.current.x = targetX;
    trackRef.current.style.transform = `translateX(${targetX}px)`;
    updateSlideOpacities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height]);

  // ── Navigation (arrows — guarded + throttled) ──

  const goNext = useCallback(() => {
    // Ground-truth guard: a transition only truly blocks if a live anime
    // instance is running. A rotation kills the tween via anime.remove() which
    // never fires `complete`, so isTransitioning can stay stuck true with no
    // animation — treat that as free (else the first post-rotation tap is a
    // no-op that only flashes the button's :active color).
    if ((isTransitioningRef.current && animationRef.current) || isDragging.current)
      return;
    isTransitioningRef.current = false;
    const now = Date.now();
    if (now - lastNavTimeRef.current < NAV_THROTTLE_MS) return;
    lastNavTimeRef.current = now;
    isTransitioningRef.current = true;
    setCurrentSlide((prev) => prev + 1);
  }, []);

  const goPrev = useCallback(() => {
    // See goNext: block only on a live animation, not a stale stuck guard.
    if ((isTransitioningRef.current && animationRef.current) || isDragging.current)
      return;
    isTransitioningRef.current = false;
    const now = Date.now();
    if (now - lastNavTimeRef.current < NAV_THROTTLE_MS) return;
    lastNavTimeRef.current = now;
    isTransitioningRef.current = true;
    setCurrentSlide((prev) => prev - 1);
  }, []);

  // Pagination dot click — guarded against teleport window and throttled
  const goToSlide = useCallback(
    (index) => {
      if (isDragging.current) return;
      // Only a live clone-teleport blocks; a stuck isJumping with no running
      // animation is stale (e.g. a wrap interrupted by rotation) — see goNext.
      if (isJumpingRef.current && animationRef.current) return;
      isJumpingRef.current = false;
      if (index === currentSlide) return;
      if (!trackRef.current) return;
      const now = Date.now();
      if (now - lastNavTimeRef.current < NAV_THROTTLE_MS) return;
      lastNavTimeRef.current = now;
      if (animationRef.current) {
        anime.remove(trackPosObj.current);
        animationRef.current = null;
      }
      const slideWidth =
        slideWidthRef.current || readSlideWidth(sliderRef.current);
      const slideEl = trackRef.current.children[index + 1];
      const targetX = slideEl
        ? -slideEl.offsetLeft
        : -((index + 1) * slideWidth);
      const direction = index > currentSlide ? 1 : -1;
      const offsetX = targetX + direction * slideWidth * 0.35;
      trackPosObj.current.x = offsetX;
      trackRef.current.style.transform = `translateX(${offsetX}px)`;
      updateSlideOpacities();
      isPaginationRef.current = true;
      isTransitioningRef.current = true;
      setCurrentSlide(index);
    },
    [currentSlide, updateSlideOpacities, readSlideWidth],
  );

  // ── Drag / Swipe handlers (Pointer Events — mouse + touch + pen) ──

  const handleDragStart = useCallback((e) => {
    if (isTransitioningRef.current) return;
    if (e.button > 0) return;
    const clientX = e.clientX;
    isDragging.current = true;
    dragStartX.current = clientX;
    dragCurrentX.current = clientX;
    dragStartTime.current = Date.now();
    dragStartContainerX.current = trackPosObj.current.x;
    if (animationRef.current) {
      anime.remove(trackPosObj.current);
      animationRef.current = null;
    }
    if (sliderRef.current) {
      dragPointerId.current = e.pointerId;
      sliderRef.current.setPointerCapture?.(e.pointerId);
      sliderRef.current.style.cursor = "grabbing";
    }
  }, []);

  const handleDragMove = useCallback(
    (e) => {
      if (!isDragging.current || !trackRef.current || !sliderRef.current)
        return;
      const clientX = e.clientX;
      dragCurrentX.current = clientX;
      const dragDelta = clientX - dragStartX.current;
      const newX = dragStartContainerX.current + dragDelta;
      trackPosObj.current.x = newX;
      trackRef.current.style.transform = `translateX(${newX}px)`;
      updateSlideOpacities();
    },
    [updateSlideOpacities],
  );

  const handleDragEnd = useCallback(() => {
    if (!isDragging.current) return;

    const dragDistance = dragCurrentX.current - dragStartX.current;
    const dragDuration = Date.now() - dragStartTime.current;
    const velocity = Math.abs(dragDistance / Math.max(dragDuration, 1));

    const SWIPE_THRESHOLD = 50;
    const VELOCITY_THRESHOLD = 0.3;

    isDragging.current = false;
    if (sliderRef.current) {
      if (dragPointerId.current != null) {
        try {
          sliderRef.current.releasePointerCapture?.(dragPointerId.current);
        } catch {
          /* capture already released */
        }
      }
      sliderRef.current.style.cursor = "grab";
    }
    dragPointerId.current = null;

    if (
      Math.abs(dragDistance) > SWIPE_THRESHOLD ||
      velocity > VELOCITY_THRESHOLD
    ) {
      isTransitioningRef.current = true;
      if (dragDistance > 0) {
        setCurrentSlide((prev) => prev - 1);
      } else {
        setCurrentSlide((prev) => prev + 1);
      }
    } else {
      isTransitioningRef.current = true;
      animationRef.current = anime({
        targets: trackPosObj.current,
        x: getTargetX(currentSlide),
        duration: 300,
        easing: "cubicBezier(.34, 1.30, .64, 1)",
        update: () => {
          if (trackRef.current) {
            trackRef.current.style.transform = `translateX(${trackPosObj.current.x}px)`;
          }
          updateSlideOpacities();
        },
        complete: () => {
          animationRef.current = null;
          isTransitioningRef.current = false;
        },
      });
    }

    dragStartX.current = null;
    dragCurrentX.current = null;
    dragStartTime.current = null;
    dragStartContainerX.current = null;
  }, [currentSlide, getTargetX, updateSlideOpacities]);

  // ── iOS fullscreen guard reset + resync on reappear ──
  // The overlay is hidden (visibility:hidden) while the iOS fullscreen spine is
  // up, then shown again when it closes — typically ACROSS an orientation change
  // (portrait → tilt → overlay → close → landscape). Two things break across
  // that gap and both make the first tap afterwards a dead no-op:
  //   1) A gesture/tween interrupted while hidden never fires its pointerup or
  //      anime `complete`, so isDragging / isTransitioning / isJumping stay stuck
  //      true and block goNext/goPrev/goToSlide.
  //   2) A clone-wrap killed mid-flight leaves currentSlide on a clone boundary
  //      (>= TOTAL_SLIDES or < 0) — the dots and the visible slide drift apart
  //      ("loses track of the current slide"), and the next tap navigates off a
  //      bogus index.
  // So every time the flag flips, hard-reset all interaction guards, and when the
  // overlay REAPPEARS, normalize a drifted index and re-home the track so the
  // slider always comes back in a known-good, immediately-tappable state.
  useEffect(() => {
    isDragging.current = false;
    isTransitioningRef.current = false;
    isPaginationRef.current = false;
    if (animationRef.current) {
      anime.remove(trackPosObj.current);
      animationRef.current = null;
    }
    if (sliderRef.current) {
      if (dragPointerId.current != null) {
        try {
          sliderRef.current.releasePointerCapture?.(dragPointerId.current);
        } catch {
          /* capture already released */
        }
      }
      sliderRef.current.style.cursor = "grab";
    }
    dragPointerId.current = null;

    if (!isHidden && sliderRef.current && trackRef.current) {
      const normalizedSlide =
        currentSlide < 0
          ? TOTAL_SLIDES - 1
          : currentSlide >= TOTAL_SLIDES
            ? 0
            : currentSlide;
      slideWidthRef.current = readSlideWidth(sliderRef.current);
      const targetX = getTargetX(normalizedSlide);
      trackPosObj.current.x = targetX;
      trackRef.current.style.transform = `translateX(${targetX}px)`;
      updateSlideOpacities();
      if (normalizedSlide !== currentSlide) {
        // Snap (not animate) the state back onto a real slide.
        isJumpingRef.current = true;
        setCurrentSlide(normalizedSlide);
      } else {
        isJumpingRef.current = false;
      }
    } else {
      isJumpingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHidden]);

  // Pagination dots — slight size/gap bump for readability
  const paginationDotSize = Math.round(chrome.dot * 1.1);
  const paginationDotGap = Math.round(chrome.dot * 3.3);
  const landscapeArrowIconMul = PAYTABLE_TUNED_LANDSCAPE_CHROME.arrowIconMul;
  // Wide desktop gets an extra arrow bump on top of the landscape icon mul.
  const wideDesktopArrowMul = isWideDesktop
    ? PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopArrowMul
    : 1;
  const baseArrowLayout = getPaytableArrowLayout(chrome);
  const arrowInset = baseArrowLayout.inset;
  const arrowIconWidth = isTunedMobileLandscape
    ? Math.round(
        baseArrowLayout.iconWidth * landscapeArrowIconMul * wideDesktopArrowMul,
      )
    : baseArrowLayout.iconWidth;
  const arrowIconHeight = isTunedMobileLandscape
    ? Math.round(
        baseArrowLayout.iconHeight *
          landscapeArrowIconMul *
          wideDesktopArrowMul,
      )
    : baseArrowLayout.iconHeight;
  const fallbackLandscapeSideOffset =
    contentWidth != null
      ? getPaytableTunedLandscapeSideOffset(
          chrome,
          Math.max(0, Math.round((dimensions.width - contentWidth) / 2)),
        )
      : null;
  const chromeSideOffset =
    landscapeChromeSideOffset ?? fallbackLandscapeSideOffset;
  const arrowLeftOffset =
    chromeSideOffset != null
      ? chromeSideOffset
      : arrowInset;
  const arrowRightOffset =
    chromeSideOffset != null
      ? chromeSideOffset
      : arrowInset;
  const landscapeArrowDownOffset = isTunedMobileLandscape
    ? Math.round(chrome.base * 1.15)
    : 0;
  // Extra downward nudge (% of viewport height) for wide desktop only.
  const wideDesktopArrowDownPct = isWideDesktop
    ? PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopArrowDownPct
    : 0;

  // ── Memoized slider stylesheet — carousel engine only ──
  const sliderStyles = useMemo(
    () => `
    .spt-content {
      flex: 1;
      min-height: 0;
      position: relative;
      width: calc(100% - ${slideAreaPad * 2}px);
      max-width: ${contentWidth ?? toolbar.width}px;
      margin: 0 auto;
      overflow: visible;
      touch-action: none;
      user-select: none;
      -webkit-user-select: none;
      cursor: grab;
    }
    .spt-track {
      display: flex;
      width: 100%; height: 100%;
      will-change: transform;
      /* iOS: harden the GPU layer so sub-pixel translateX doesn't leave a hairline seam */
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
    }
    .spt-slide {
      flex: 0 0 100%;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      height: 100%;
      box-sizing: border-box;
      display: flex; align-items: stretch; justify-content: center;
      pointer-events: none;
      position: relative;
    }
    .spt-slide.active {
      pointer-events: auto;
    }

    /* Navigation arrows — viewport vertical center (not carousel box) */
    .spt-arrow {
      position: fixed; top: calc(50% + ${landscapeArrowDownOffset}px + ${wideDesktopArrowDownPct}%);
      transform: translateY(-50%);
      background: none; border: none; cursor: pointer;
      padding: 0; outline: none;
      -webkit-tap-highlight-color: transparent;
      display: flex; align-items: center; justify-content: center;
      transition: transform 0.1s ease;
      z-index: 1003;
    }
    .spt-arrow-icon {
      height: ${arrowIconHeight}px;
      width: ${arrowIconWidth}px;
      pointer-events: none;
      background-color: #ffc102;
      -webkit-mask-size: contain;
      -webkit-mask-repeat: no-repeat;
      -webkit-mask-position: center;
      mask-size: contain;
      mask-repeat: no-repeat;
      mask-position: center;
      transition: background-color 0.2s ease;
    }
    .spt-arrow-left { left: ${arrowLeftOffset}px; }
    .spt-arrow-right { right: ${arrowRightOffset}px; }
    /* Hover only on real pointer devices — on iOS touch a :hover rule makes the
       arrow eat the first tap (paints white, no click) after the overlay
       covers/uncovers across a rotation. Gating it keeps the arrow tappable. */
    @media (hover: hover) {
      .spt-arrow:hover .spt-arrow-icon { background-color: #ffffff; }
    }
    .spt-arrow:active { transform: translateY(-50%) scale(0.85); }
    .spt-arrow:active .spt-arrow-icon { background-color: #ffffff; }

    /* Footer — pagination dots */
    .spt-footer {
      flex-shrink: 0;
      display: flex;
      justify-content: center;
      margin-top: ${footerMarginTop}px;
    }
    .spt-dots {
      display: flex; gap: ${paginationDotGap}px; align-items: center;
    }
    .spt-dot {
      border-radius: 50%; border: none; outline: none;
      background: #ffffff; cursor: pointer;
      transition: all 0.2s ease; padding: 0;
    }
    .spt-dot.active { background: #ffc102; }
    .spt-dot:hover { background: #ffc102; }

    /* Decorative glow — landscape/desktop, centered in each slide */
    .spt-slide-glow {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 80%;
      pointer-events: none;
      z-index: 0;
      opacity: 1;
    }
  `,
    [
      slideAreaPad,
      contentWidth,
      toolbar.width,
      paginationDotGap,
      arrowLeftOffset,
      arrowRightOffset,
      landscapeArrowDownOffset,
      wideDesktopArrowDownPct,
      arrowIconWidth,
      arrowIconHeight,
      footerMarginTop,
    ],
  );

  // ── Compute realIndex for dots (maps clone positions to real index) ──
  const realIndex =
    currentSlide < 0
      ? TOTAL_SLIDES - 1
      : currentSlide >= TOTAL_SLIDES
        ? 0
        : currentSlide;

  return (
    <>
      {/* ── CONTENT: carousel viewport ── */}
      <div
        className="spt-content"
        ref={sliderRef}
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerCancel={handleDragEnd}
        onLostPointerCapture={handleDragEnd}
      >
        <div className="spt-track" ref={trackRef}>
          {/* Clone of last slide (for infinite wrap backward) */}
          <PaytableSlide isPortrait={isPortrait} glowSrc={glowSrc}>
            {slides[TOTAL_SLIDES - 1]()}
          </PaytableSlide>
          {/* Real slides */}
          {slides.map((renderFn, i) => (
            <PaytableSlide
              key={i}
              isActive={i === realIndex}
              isPortrait={isPortrait}
              glowSrc={glowSrc}
            >
              {renderFn()}
            </PaytableSlide>
          ))}
          {/* Clone of first slide (for infinite wrap forward) */}
          <PaytableSlide isPortrait={isPortrait} glowSrc={glowSrc}>
            {slides[0]()}
          </PaytableSlide>
        </div>
      </div>

      {/* Navigation arrows — fixed to viewport vertical center */}
      <button
        className="spt-arrow spt-arrow-left"
        aria-label="Previous slide"
        // Navigate on pointerup, not just click: right after the iOS fullscreen
        // overlay is dismissed, Safari swallows the FIRST synthesized click on
        // the freshly-interactive paytable (the "needs a double tap once" bug).
        // pointerup fires reliably regardless; the throttle + transition guard
        // in goPrev dedupe so mouse (pointerup + click) never double-navigates.
        // onClick stays for keyboard (Enter/Space) activation.
        onClick={goPrev}
        onPointerUp={(e) => e.button === 0 && goPrev()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <div
          className="spt-arrow-icon"
          style={{
            WebkitMaskImage: `url('${getUITextureAsDataURL("chevron_left")}')`,
            maskImage: `url('${getUITextureAsDataURL("chevron_left")}')`,
          }}
        />
      </button>
      <button
        className="spt-arrow spt-arrow-right"
        aria-label="Next slide"
        // See spt-arrow-left: pointerup avoids the swallowed-first-click on iOS
        // after the fullscreen overlay closes; throttle/guard prevent double-nav.
        onClick={goNext}
        onPointerUp={(e) => e.button === 0 && goNext()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <div
          className="spt-arrow-icon"
          style={{
            WebkitMaskImage: `url('${getUITextureAsDataURL("chevron_right")}')`,
            maskImage: `url('${getUITextureAsDataURL("chevron_right")}')`,
          }}
        />
      </button>

      {/* ── FOOTER: pagination dots ── */}
      <div className="spt-footer">
        <div className="spt-dots">
          {Array.from({ length: TOTAL_SLIDES }, (_, i) => (
            <button
              key={i}
              className={`spt-dot ${realIndex === i ? "active" : ""}`}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => goToSlide(i)}
              style={{ width: paginationDotSize, height: paginationDotSize }}
            />
          ))}
        </div>
      </div>

      {/* Slider-scoped styles */}
      <style>{sliderStyles}</style>
    </>
  );
};

export default PaytableSlider;
